import React from 'react';
import { useTranslation } from 'react-i18next';

import { NOTIF_TYPES } from '@private/notifications';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import api from 'api';
import { CLIENTS_KEYS } from 'api/clients/constants';
import {
  IClient,
  IClientACPM,
  IClientACPMPayload,
  IClientForm,
  IClientGeneralInfo,
  IClientInstruments,
  IClientInstrumentStatusForm,
  IClientPageLimitPayload,
  IClientPageLimits,
  IClientPaymentMethod,
  IClientPriorityRuleset,
  IClientPriorityRulesetPayload,
  IClientRoutingPaymentInstrument,
  IClientRoutingPriority,
  IClientRoutingStats,
  IClientSavedCardToken,
  IClientSavedCardTokenDeletePayload,
  IClientsLimits,
  IClientsLimitsPayload,
  IClientStats,
  IClientTopErrors,
  ICreateClientRoutingPrioritiesPayload,
  IEditClientPayload,
  IEditClientRoutingPriorityPayload,
  IPaymentInstrument,
  IPaymentInstrumentPayload,
  IUpdatePaymentInstrumentPayload,
} from 'api/clients/types';
import { STATUSES } from 'constants/common';
import { normalizeQueryKey, useGetAllEntities } from 'queries/utils';
import { getInlineEditErrorMessage, getQueryBoundary } from 'utils/common';
import { notificationService } from 'utils/notificationService';
import {
  IAllPageFilters,
  IBaseGist,
  INullable,
  IPageType,
  QueryError,
} from 'utils/types';

export const useGetClients = (
  pageFilters: IAllPageFilters,
): UseQueryResult<IPageType<IClient>> =>
  useGetAllEntities<IClient>({
    getAllEntitiesFunc: api.clients.getAllClients,
    pageFilters,
    queryKey: CLIENTS_KEYS.ALL,
  });

export const useGetClientGeneralInfo = (
  id?: string,
): UseQueryResult<IClientGeneralInfo> => {
  const getClientDetails = React.useCallback(
    () => (id ? api.clients.getClientsGeneralInfo(id) : null),
    [id],
  );

  return useQuery({
    queryFn: getClientDetails,
    queryKey: normalizeQueryKey([CLIENTS_KEYS.CLIENT_INFO, id]),
  });
};

export const useGetClientStats = (id?: string): UseQueryResult<IClientStats> => {
  const getClientStats = React.useCallback(
    () => (id ? api.clients.getClientStats(id) : null),
    [id],
  );

  return useQuery({
    queryFn: getClientStats,
    queryKey: normalizeQueryKey([CLIENTS_KEYS.CLIENT_STATS, id]),
  });
};

export const useGetClientTopErrors = (id?: string): UseQueryResult<IClientTopErrors> => {
  const getClientErrors = React.useCallback(
    () => (id ? api.clients.getClientTopErrors(id) : null),
    [id],
  );
  return useQuery({
    queryFn: getClientErrors,
    queryKey: normalizeQueryKey([CLIENTS_KEYS.CLIENT_TOP_ERRORS, id]),
  });
};

export const useGetClientPaymentMethod = (
  id?: string,
): UseQueryResult<IClientPaymentMethod> => {
  const getClientPaymentMethod = React.useCallback(
    () => (id ? api.clients.getClientPaymentMethod(id) : null),
    [id],
  );
  return useQuery({
    queryFn: getClientPaymentMethod,
    queryKey: normalizeQueryKey([CLIENTS_KEYS.CLIENT_PAYMENT_METHOD, id]),
  });
};

export const useCreateClient = (): UseMutationResult<
  IClientGeneralInfo,
  QueryError,
  IClientForm,
  { ntfID: string }
> => {
  const { t } = useTranslation();

  return useMutation({
    mutationFn: api.clients.createClient,
    onError: (error: QueryError, _variables, context) => {
      const isSavingError = error?.response?.status === STATUSES.INVALID_DATA;
      const errorsExists = Object.keys(error?.response?.data?.errors || {}).length;

      if (errorsExists) {
        notificationService.show({
          message:
            error?.response?.data?.message ||
            (isSavingError
              ? t('notifications.invalidData')
              : t('client.form.notifications.create.error')),
          title: t('notifications.errorTitle'),
          type: NOTIF_TYPES.ERROR,
          updateId: context?.ntfID,
        });
      } else {
        notificationService.remove(context?.ntfID || '');
      }
    },
    onMutate: () => {
      const ntfID = notificationService.show({
        message: t('client.form.notifications.create.loading'),
        title: t('notifications.loadingTitle'),
        type: NOTIF_TYPES.LOADING,
      });

      return { ntfID };
    },
    onSuccess: (_response, _, context) => {
      notificationService.show({
        message: t('client.form.notifications.create.success'),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: context?.ntfID,
      });
    },
  });
};

export const useEditClient = (): UseMutationResult<
  IClientGeneralInfo,
  QueryError,
  IEditClientPayload,
  { ntfID: string }
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.clients.editClient,
    onError: (error: QueryError, _variables, context) => {
      const isSavingError = error?.response?.status === STATUSES.INVALID_DATA;
      const errorsExists = Object.keys(error?.response?.data?.errors || {}).length;

      if (errorsExists) {
        notificationService.show({
          message:
            error?.response?.data?.message ||
            (isSavingError
              ? t('notifications.invalidData')
              : t('client.form.notifications.edit.error')),
          title: t('notifications.errorTitle'),
          type: NOTIF_TYPES.ERROR,
          updateId: context?.ntfID,
        });
      } else {
        notificationService.remove(context?.ntfID || '');
      }
    },
    onMutate: () => {
      const ntfID = notificationService.show({
        message: t('client.form.notifications.edit.loading'),
        title: t('notifications.loadingTitle'),
        type: NOTIF_TYPES.LOADING,
      });

      return { ntfID };
    },
    onSuccess: async (_response, { id }, context) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: normalizeQueryKey([CLIENTS_KEYS.ALL]),
        }),
        queryClient.invalidateQueries({
          queryKey: normalizeQueryKey([CLIENTS_KEYS.CLIENT_INFO, id]),
        }),
      ]);

      notificationService.show({
        message: t('client.form.notifications.edit.success'),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: context?.ntfID,
      });
    },
  });
};

export const useGetClientPageLimits = (
  pageFilters: IAllPageFilters,
): UseQueryResult<IPageType<IClientPageLimits>> =>
  useGetAllEntities<IClientPageLimits>({
    getAllEntitiesFunc: api.clients.getClientPageLimits,
    pageFilters,
    queryKey: CLIENTS_KEYS.CLIENT_PAGE_LIMITS,
  });

export const useGetClientPageLimit = (
  id?: INullable<number>,
): UseQueryResult<IClientPageLimits> => {
  const getClientLimitDetails = React.useCallback(
    () => (id ? api.clients.getClientPageLimit(id) : null),
    [id],
  );

  return useQuery({
    queryFn: getClientLimitDetails,
    queryKey: normalizeQueryKey([CLIENTS_KEYS.CLIENT_PAGE_LIMIT, id]),
  });
};

export const useCreateClientPageLimit = (): UseMutationResult<
  IBaseGist,
  QueryError,
  IClientPageLimitPayload
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const toastId = 'CreateClientPageLimit';
  const ntfPrefix = 'client.cashierPageLimit.notifications.create';

  return useMutation({
    mutationFn: api.clients.createClientPageLimit,
    onError: (error: QueryError) => {
      notificationService.show({
        message: error?.response?.data?.message || t(`${ntfPrefix}.error`),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: toastId,
      });
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([CLIENTS_KEYS.CLIENT_PAGE_LIMITS]),
      });

      notificationService.show({
        message: t(`${ntfPrefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
    throwOnError: (error: QueryError) => getQueryBoundary(error, t, toastId),
  });
};

export const useEditClientPageLimit = (): UseMutationResult<
  IBaseGist,
  QueryError,
  IClientPageLimitPayload
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const toastId = 'EditClientPageLimit';
  const ntfPrefix = 'client.cashierPageLimit.notifications.edit';

  return useMutation({
    mutationFn: api.clients.editClientPageLimit,
    onError: (error: QueryError) => {
      notificationService.show({
        message: error?.response?.data?.message || t(`${ntfPrefix}.error`),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: toastId,
      });
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([CLIENTS_KEYS.CLIENT_PAGE_LIMITS]),
      });
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([CLIENTS_KEYS.CLIENT_PAGE_LIMIT]),
      });

      notificationService.show({
        message: t(`${ntfPrefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
    throwOnError: (error: QueryError) => getQueryBoundary(error, t, toastId),
  });
};

export const useDeleteClientLimit = (): UseMutationResult<
  AxiosResponse,
  QueryError,
  number
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const toastId = 'deleteClientLimit';
  const ntfPrefix = 'client.cashierPageLimit.notifications.delete';

  return useMutation({
    mutationFn: api.clients.deleteClientLimit,
    onError: (error: QueryError) => {
      notificationService.show({
        message: error?.response?.data?.message || t(`${ntfPrefix}.error`),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: toastId,
      });
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([CLIENTS_KEYS.CLIENT_PAGE_LIMITS]),
      });

      notificationService.show({
        message: t(`${ntfPrefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
  });
};

export const useGetClientACPMs = (
  pageFilters: IAllPageFilters,
  id: string,
  enabled = true,
): UseQueryResult<IPageType<IClientACPM>> => {
  const getAllEntitiesFunc = (filters: IAllPageFilters) =>
    api.clients.getClientAllowedCashierPMs(id, filters);

  return useGetAllEntities<IClientACPM>({
    config: { enabled },
    getAllEntitiesFunc,
    pageFilters,
    queryKey: CLIENTS_KEYS.CLIENT_ALLOWED_CASHIER_PMS,
  });
};

export const useCreateClientACPM = (): UseMutationResult<
  AxiosResponse,
  QueryError,
  IClientACPMPayload
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const toastId = 'CreateClientACPM';
  const ntfPrefix = 'client.allowedPaymentMethods.notifications.create';

  return useMutation({
    mutationFn: api.clients.createClientAllowedCashierPM,
    onError: (error: QueryError) => {
      notificationService.show({
        message: error?.response?.data?.message || t(`${ntfPrefix}.error`),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: toastId,
      });
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([CLIENTS_KEYS.CLIENT_ALLOWED_CASHIER_PMS]),
      });

      notificationService.show({
        message: t(`${ntfPrefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
    throwOnError: (error: QueryError) => getQueryBoundary(error, t, toastId),
  });
};

export const useDeleteClientACPM = (): UseMutationResult<
  AxiosResponse,
  QueryError,
  number
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const toastId = 'deleteClientACPM';
  const ntfPrefix = 'client.allowedPaymentMethods.notifications.delete';

  return useMutation({
    mutationFn: api.clients.deleteClientAllowedCashierPM,
    onError: (error: QueryError) => {
      notificationService.show({
        message: error?.response?.data?.message || t(`${ntfPrefix}.error`),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: toastId,
      });
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([CLIENTS_KEYS.CLIENT_ALLOWED_CASHIER_PMS]),
      });

      notificationService.show({
        message: t(`${ntfPrefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
    throwOnError: (error: QueryError) => getQueryBoundary(error, t, toastId),
  });
};

export const useGetClientLimits = (
  id?: INullable<number | string>,
): UseQueryResult<IClientsLimits> => {
  const getClientsLimits = React.useCallback(
    () => (id ? api.clients.getClientsLimits(id) : null),
    [id],
  );

  return useQuery({
    queryFn: getClientsLimits,
    queryKey: normalizeQueryKey([CLIENTS_KEYS.CLIENTS_LIMITS, id]),
  });
};

export const useCreateClientsLimits = (): UseMutationResult<
  IBaseGist,
  QueryError,
  IClientsLimitsPayload
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const toastId = 'CreateClientsLimits';
  const ntfPrefix = 'client.clientsLimits.form.notifications.create';

  return useMutation({
    mutationFn: api.clients.createClientsLimits,
    onError: (error: QueryError) => {
      notificationService.show({
        message: error?.response?.data?.message || t(`${ntfPrefix}.error`),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: toastId,
      });
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([CLIENTS_KEYS.CLIENTS_LIMITS]),
      });

      notificationService.show({
        message: t(`${ntfPrefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
    throwOnError: (error: QueryError) => getQueryBoundary(error, t, toastId),
  });
};

export const useEditClientsLimits = (): UseMutationResult<
  IBaseGist,
  QueryError,
  IClientsLimitsPayload
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const toastId = 'EditClientsLimits';
  const ntfPrefix = 'client.clientsLimits.form.notifications.update';

  return useMutation({
    mutationFn: api.clients.editClientsLimits,
    onError: (error: QueryError) => {
      notificationService.show({
        message: error?.response?.data?.message || t(`${ntfPrefix}.error`),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: toastId,
      });
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([CLIENTS_KEYS.CLIENTS_LIMITS]),
      });

      notificationService.show({
        message: t(`${ntfPrefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
    throwOnError: (error: QueryError) => getQueryBoundary(error, t, toastId),
  });
};

export const useDeleteClientsLimits = (): UseMutationResult<
  AxiosResponse,
  QueryError,
  string
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const toastId = 'deleteClientsLimits';
  const ntfPrefix = 'client.clientsLimits.form.notifications.delete';

  return useMutation({
    mutationFn: api.clients.deleteClientsLimits,
    onError: (error: QueryError) => {
      notificationService.show({
        message: error?.response?.data?.message || t(`${ntfPrefix}.error`),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: toastId,
      });
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([CLIENTS_KEYS.CLIENTS_LIMITS]),
      });

      notificationService.show({
        message: t(`${ntfPrefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
  });
};

export const useGetClientPriorityRulesets = (
  pageFilters: IAllPageFilters,
  id: string,
  enabled = true,
  additionalKeys: string[] = [],
): UseQueryResult<IPageType<IClientPriorityRuleset>> => {
  const getAllEntitiesFunc = (filters: IAllPageFilters) =>
    api.clients.getClientPriorityRulesets(id, filters);

  return useGetAllEntities<IClientPriorityRuleset>({
    config: { enabled },
    getAllEntitiesFunc,
    pageFilters,
    queryKey: [CLIENTS_KEYS.CLIENT_PRIORITY_RULESETS, ...additionalKeys],
  });
};

export const useCreateClientPriorityRulesets = (
  id?: number | string,
): UseMutationResult<IBaseGist, QueryError, IClientPriorityRulesetPayload> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const toastId = 'CreateClientPriorityRulesets';
  const ntfPrefix = 'client.priorityRulesets.notifications.create';

  return useMutation({
    mutationFn: api.clients.createClientPriorityRulesets,
    onError: (error: QueryError) => {
      notificationService.show({
        message: error?.response?.data?.message || t(`${ntfPrefix}.error`),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: toastId,
      });
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([CLIENTS_KEYS.CLIENT_PRIORITY_RULESETS, id]),
      });

      notificationService.show({
        message: t(`${ntfPrefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
    throwOnError: (error: QueryError) => getQueryBoundary(error, t, toastId),
  });
};

export const useEditClientPriorityRulesets = (
  id?: number | string,
): UseMutationResult<IBaseGist, QueryError, IClientPriorityRulesetPayload> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const toastId = 'EditClientPriorityRulesets';
  const ntfPrefix = 'client.priorityRulesets.notifications.edit';

  return useMutation({
    mutationFn: api.clients.editClientPriorityRulesets,
    onError: (error: QueryError) => {
      notificationService.show({
        message: error?.response?.data?.message || t(`${ntfPrefix}.error`),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: toastId,
      });
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([CLIENTS_KEYS.CLIENT_PRIORITY_RULESETS, id]),
      });

      notificationService.show({
        message: t(`${ntfPrefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
    throwOnError: (error: QueryError) => getQueryBoundary(error, t, toastId),
  });
};

export const useDeletePriorityRuleset = (): UseMutationResult<
  AxiosResponse,
  QueryError,
  number
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const toastId = 'deletePriorityRuleset';
  const ntfPrefix = 'client.priorityRulesets.notifications.delete';

  return useMutation({
    mutationFn: api.clients.deleteClientPriorityRulesets,
    onError: (error: QueryError) => {
      notificationService.show({
        message: error?.response?.data?.message || t(`${ntfPrefix}.error`),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: toastId,
      });
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([CLIENTS_KEYS.CLIENT_PRIORITY_RULESETS]),
      });

      notificationService.show({
        message: t(`${ntfPrefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
  });
};

export const useGetClientRoutingPriorities = (
  pageFilters: IAllPageFilters,
  id: string,
  enabled = true,
  additionalKeys: string[] = [],
): UseQueryResult<IPageType<IClientRoutingPriority>> => {
  const getAllEntitiesFunc = (filters: IAllPageFilters) =>
    api.clients.getClientRoutingPriorities(id, filters);

  return useGetAllEntities<IClientRoutingPriority>({
    config: { enabled },
    getAllEntitiesFunc,
    pageFilters,
    queryKey: [CLIENTS_KEYS.CLIENT_ROUTING_PRIORITIES, ...additionalKeys],
  });
};

export const useCreateClientRoutingPriorities = (): UseMutationResult<
  AxiosResponse,
  QueryError,
  ICreateClientRoutingPrioritiesPayload
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const toastId = 'createClientRoutingPriorities';
  const ntfPrefix = 'client.routingPriorities.notifications.create';

  return useMutation({
    mutationFn: api.clients.createClientRoutingPriorities,
    onError: (error: QueryError) => {
      notificationService.show({
        message: error?.response?.data?.message || t(`${ntfPrefix}.error`),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: toastId,
      });
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([CLIENTS_KEYS.CLIENT_ROUTING_PRIORITIES]),
      });

      notificationService.show({
        message: t(`${ntfPrefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
    throwOnError: (error: QueryError) => getQueryBoundary(error, t, toastId),
  });
};

export const useEditClientRoutingPriority = (): UseMutationResult<
  AxiosResponse,
  QueryError,
  IEditClientRoutingPriorityPayload
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const toastId = 'editClientRoutingPriority';
  const ntfPrefix = 'client.routingPriorities.notifications.edit';

  return useMutation({
    mutationFn: api.clients.editClientRoutingPriority,
    onError: (error: QueryError) => {
      notificationService.show({
        message: error?.response?.data?.message || t(`${ntfPrefix}.error`),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: toastId,
      });
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([CLIENTS_KEYS.CLIENT_ROUTING_PRIORITIES]),
      });

      notificationService.show({
        message: t(`${ntfPrefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
    throwOnError: (error: QueryError) => getQueryBoundary(error, t, toastId),
  });
};

export const useDeleteClientRoutingPriority = (): UseMutationResult<
  AxiosResponse,
  QueryError,
  number
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const toastId = 'deleteClientRoutingPriority';
  const ntfPrefix = 'client.routingPriorities.notifications.delete';

  return useMutation({
    mutationFn: api.clients.deleteClientRoutingPriority,
    onError: (error: QueryError) => {
      notificationService.show({
        message: error?.response?.data?.message || t(`${ntfPrefix}.error`),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: toastId,
      });
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([CLIENTS_KEYS.CLIENT_ROUTING_PRIORITIES]),
      });

      notificationService.show({
        message: t(`${ntfPrefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
    throwOnError: (error: QueryError) => getQueryBoundary(error, t, toastId),
  });
};

export const useGetUniquePaymentInstruments = (
  props: IPaymentInstrumentPayload,
): UseQueryResult<IPaymentInstrument[]> => {
  const { id, transactionType } = props;

  const getClientsPaymentInstruments = React.useCallback(
    () => (id && transactionType ? api.clients.getUniquePaymentInstruments(props) : null),
    [id, props, transactionType],
  );

  return useQuery({
    queryFn: getClientsPaymentInstruments,
    queryKey: normalizeQueryKey([
      CLIENTS_KEYS.CLIENTS_UNIQUE_PAYMENT_INSTRUMENTS,
      id,
      transactionType,
    ]),
  });
};

export const useUpdatePaymentInstrument = ({
  id,
  transactionType,
}: IPaymentInstrumentPayload): UseMutationResult<
  AxiosResponse,
  QueryError,
  IUpdatePaymentInstrumentPayload
> => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();
  const prefix = 'client.paymentInstruments.notifications.update';
  const toastId = 'updatePaymentInstrument';

  return useMutation({
    mutationFn: api.clients.updateUniquePaymentInstrument,
    onError: (error: QueryError) => {
      notificationService.show({
        message: getInlineEditErrorMessage(error, t, t(`${prefix}.error`)),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: toastId,
      });
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${prefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: () => {
      if (id && transactionType) {
        queryClient.invalidateQueries({
          queryKey: normalizeQueryKey([
            CLIENTS_KEYS.CLIENTS_UNIQUE_PAYMENT_INSTRUMENTS,
            id,
            transactionType,
          ]),
        });
      }

      notificationService.show({
        message: t(`${prefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
    throwOnError: false,
  });
};

export const useGetClientRoutingStats = (
  id?: string,
): UseQueryResult<IClientRoutingStats> => {
  const getClientRoutingStats = React.useCallback(
    () => (id ? api.clients.getClientRoutingStats(id) : null),
    [id],
  );

  return useQuery({
    queryFn: getClientRoutingStats,
    queryKey: normalizeQueryKey([CLIENTS_KEYS.CLIENT_ROUTING_STATS, id]),
  });
};

export const useGetClientRoutingPaymentInstruments = ({
  id,
  pageFilters,
}: {
  id: string;
  pageFilters: IAllPageFilters;
}): UseQueryResult<IPageType<IClientRoutingPaymentInstrument>> =>
  useGetAllEntities<IClientRoutingPaymentInstrument>({
    getAllEntitiesFunc: (filters: IAllPageFilters) =>
      api.clients.getClientRoutingPaymentInstruments({ id, pageFilters: filters }),
    pageFilters,
    queryKey: [CLIENTS_KEYS.CLIENT_ROUTING_PAYMENT_INSTRUMENTS, id],
  });

export const useGetClientSavedCardTokens = ({
  id,
  pageFilters,
}: {
  id: string;
  pageFilters: IAllPageFilters;
}): UseQueryResult<IPageType<IClientSavedCardToken>> =>
  useGetAllEntities<IClientSavedCardToken>({
    getAllEntitiesFunc: (filters: IAllPageFilters) =>
      api.clients.getClientSavedCardTokens({ id, pageFilters: filters }),
    pageFilters,
    queryKey: [CLIENTS_KEYS.CLIENT_SAVED_CARD_TOKENS, id],
  });

export const useDeleteClientSavedCardTokens = (): UseMutationResult<
  AxiosResponse,
  QueryError,
  IClientSavedCardTokenDeletePayload
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const toastId = 'deleteClientSavedCardTokens';
  const ntfPrefix = 'client.savedCardTokens.notifications.delete';

  return useMutation({
    mutationFn: api.clients.deleteClientSavedCardTokens,
    onError: (error: QueryError) => {
      notificationService.show({
        message: error?.response?.data?.message || t(`${ntfPrefix}.error`),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: toastId,
      });
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: (_, { customerId }) => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([CLIENTS_KEYS.CLIENT_SAVED_CARD_TOKENS, customerId]),
      });

      notificationService.show({
        message: t(`${ntfPrefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
  });
};

export const useRecalculateClientStats = (
  id?: number | string,
): UseMutationResult<AxiosResponse, QueryError, number | string> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const toastId = 'recalculateClientStats';
  const ntfPrefix = 'client.routingStats.stats.notifications.recalculate';

  return useMutation({
    mutationFn: api.clients.recalculateClientStats,
    onError: (error: QueryError) => {
      notificationService.show({
        message: error?.response?.data?.message || t(`${ntfPrefix}.error`),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: toastId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([CLIENTS_KEYS.CLIENT_STATS, id]),
      });
    },
  });
};

export const useGetClientsInstruments = (
  pageFilters: IAllPageFilters,
): UseQueryResult<IPageType<IClientInstruments>> =>
  useGetAllEntities<IClientInstruments>({
    getAllEntitiesFunc: api.clients.getAllClientsInstruments,
    pageFilters,
    queryKey: CLIENTS_KEYS.CLIENTS_ALL_INSTRUMENTS,
  });

export const useEditClientInstrument = (): UseMutationResult<
  IBaseGist,
  QueryError,
  IClientInstrumentStatusForm
> => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const toastId = 'EditClientInstrument';
  const ntfPrefix = 'clientsInstruments.notifications.update';

  return useMutation({
    mutationFn: api.clients.updateClientInstrument,
    onError: (error: QueryError) => {
      notificationService.show({
        message: error?.response?.data?.message || t(`${ntfPrefix}.error`),
        title: t('notifications.errorTitle'),
        type: NOTIF_TYPES.ERROR,
        updateId: toastId,
      });
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${ntfPrefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: (_response, data) => {
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([CLIENTS_KEYS.CLIENTS_ALL_INSTRUMENTS]),
      });
      queryClient.invalidateQueries({
        queryKey: normalizeQueryKey([
          CLIENTS_KEYS.CLIENT_SAVED_CARD_TOKENS,
          data.customerId,
        ]),
      });

      notificationService.show({
        message: t(`${ntfPrefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
    throwOnError: (error: QueryError) => getQueryBoundary(error, t, toastId),
  });
};
